// AdminPathways — Phase 6. Create ordered sequences of courses for personas/cohorts.
import React, { useCallback, useEffect, useState } from 'react';
import { api } from '@/lib/api';
import { toast } from 'sonner';
import { Trash2, Loader2, Route, Plus, GripVertical } from 'lucide-react';

const PERSONAS = ['all', 'electrician', 'retailer', 'distributor', 'channel_partner', 'technician', 'trainee'];

export default function AdminPathways() {
    const [pathways, setPathways] = useState([]);
    const [courses, setCourses] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selected, setSelected] = useState([]);
    const [name, setName] = useState('');
    const [desc, setDesc] = useState('');
    const [persona, setPersona] = useState('all');
    const [saving, setSaving] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const [p, c] = await Promise.all([api.get('/admin/pathways'), api.get('/admin/videos')]);
            setPathways(p.data || []);
            setCourses(c.data || []);
        } catch { toast.error('Failed to load'); } finally { setLoading(false); }
    }, []);
    useEffect(() => { load(); }, [load]);

    const toggle = (id) => setSelected(s => s.includes(id) ? s.filter(x => x !== id) : [...s, id]);
    const move = (i, dir) => {
        const next = [...selected];
        const j = i + dir;
        if (j < 0 || j >= next.length) return;
        [next[i], next[j]] = [next[j], next[i]];
        setSelected(next);
    };

    const save = async () => {
        if (!name.trim() || !selected.length) { toast.error('Name and at least 1 course required'); return; }
        setSaving(true);
        try {
            await api.post('/admin/pathways', { name, description: desc, persona, course_ids: selected, is_published: true });
            setName(''); setDesc(''); setSelected([]); setPersona('all');
            toast.success('Pathway created');
            load();
        } catch (e) { toast.error(e?.response?.data?.detail || 'Save failed'); } finally { setSaving(false); }
    };

    const del = async (id) => {
        if (!window.confirm('Delete pathway?')) return;
        try { await api.delete(`/admin/pathways/${id}`); load(); } catch { toast.error('Delete failed'); }
    };

    const titleOf = (cid) => (courses.find(c => c.id === cid) || {}).title || cid;

    return (
        <div className="space-y-6" data-testid="admin-pathways-page">
            <div>
                <h1 className="font-display font-extrabold text-2xl text-slate-900">Training Pathways</h1>
                <p className="text-sm text-slate-500">Curate ordered sequences of Courses per persona or cohort.</p>
            </div>

            <div className="grid lg:grid-cols-2 gap-5">
                <div className="bg-white rounded-2xl ring-1 ring-slate-200 shadow-sm p-5 space-y-3">
                    <div className="flex items-center gap-2 font-bold text-slate-800"><Plus className="w-4 h-4" /> New Pathway</div>
                    <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Pathway name (e.g. New Electrician 30-day kickstart)" className={inputCls} data-testid="pw-name" />
                    <textarea value={desc} onChange={(e) => setDesc(e.target.value)} placeholder="Description" rows="2" className={inputCls} data-testid="pw-desc" />
                    <div className="flex items-center gap-2">
                        <label className="text-xs font-bold text-slate-600">Persona</label>
                        <select value={persona} onChange={(e) => setPersona(e.target.value)} className={inputCls + ' max-w-xs'} data-testid="pw-persona">
                            {PERSONAS.map(p => <option key={p} value={p}>{p}</option>)}
                        </select>
                    </div>
                    <div>
                        <div className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-1">Available courses</div>
                        <div className="max-h-44 overflow-y-auto space-y-1 ring-1 ring-slate-200 rounded-lg p-2">
                            {courses.filter(c => !selected.includes(c.id)).map(c => (
                                <button key={c.id} onClick={() => toggle(c.id)} className="w-full text-left text-xs px-2 py-1.5 rounded hover:bg-amber-50" data-testid={`pw-add-${c.id}`}>
                                    + {c.title}
                                </button>
                            ))}
                            {courses.length === 0 && <div className="text-xs text-slate-400 italic">No courses yet — add some under Courses</div>}
                        </div>
                    </div>
                    <div>
                        <div className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-1">Selected order</div>
                        <div className="space-y-1 ring-1 ring-slate-200 rounded-lg p-2 min-h-[60px]">
                            {selected.map((cid, i) => (
                                <div key={cid} className="flex items-center gap-2 px-2 py-1 bg-amber-50 rounded">
                                    <GripVertical className="w-3 h-3 text-slate-400" />
                                    <span className="flex-1 text-xs truncate">{i + 1}. {titleOf(cid)}</span>
                                    <button onClick={() => move(i, -1)} className="text-xs text-slate-500 hover:text-slate-800">↑</button>
                                    <button onClick={() => move(i, 1)} className="text-xs text-slate-500 hover:text-slate-800">↓</button>
                                    <button onClick={() => toggle(cid)} className="text-rose-600"><Trash2 className="w-3 h-3" /></button>
                                </div>
                            ))}
                            {selected.length === 0 && <div className="text-xs text-slate-400 italic px-1">Pick a course above</div>}
                        </div>
                    </div>
                    <button onClick={save} disabled={saving}
                        className="w-full py-2.5 rounded-xl bg-amber-500 hover:bg-amber-600 text-slate-900 font-bold text-sm disabled:opacity-50"
                        data-testid="pw-save">
                        {saving ? 'Saving…' : 'Save Pathway'}
                    </button>
                </div>

                <div className="bg-white rounded-2xl ring-1 ring-slate-200 shadow-sm p-5">
                    <div className="flex items-center gap-2 mb-3 font-bold text-slate-800"><Route className="w-4 h-4" /> Live Pathways ({pathways.length}) {loading && <Loader2 className="w-3 h-3 animate-spin text-slate-400" />}</div>
                    {pathways.length === 0 && !loading && <div className="text-xs text-slate-400 italic">None yet.</div>}
                    <div className="space-y-2">
                        {pathways.map(p => (
                            <div key={p.id} className="ring-1 ring-slate-200 rounded-xl p-3" data-testid={`pw-row-${p.id}`}>
                                <div className="flex items-center justify-between gap-2">
                                    <div>
                                        <div className="font-bold text-sm">{p.name}</div>
                                        <div className="text-[10px] text-slate-500 uppercase tracking-wider">{p.persona} · {p.course_ids.length} courses</div>
                                    </div>
                                    <button onClick={() => del(p.id)} className="text-rose-600 hover:text-rose-800" data-testid={`pw-del-${p.id}`}><Trash2 className="w-4 h-4" /></button>
                                </div>
                                <ol className="mt-2 text-xs text-slate-600 list-decimal pl-4 space-y-0.5">
                                    {p.course_ids.map(cid => <li key={cid}>{titleOf(cid)}</li>)}
                                </ol>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        </div>
    );
}

const inputCls = 'w-full px-3 py-2 rounded-lg ring-1 ring-slate-300 focus:ring-2 focus:ring-amber-400 focus:outline-none text-sm bg-white';
